import React, { useEffect, useState } from 'react';

import { getDownloadURL, ref } from 'firebase/storage';

import { Link } from 'react-router';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/material/styles';
import { useColorScheme } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { useData } from '../../contextProviders/DataProvider';
import { storage } from '../../firebase';

interface SettingsMenuItem {
  name: string;
  to: string;
  passUserId: boolean;
  showLogin: boolean;
  showLogout: boolean;
  showOnlyAdmin: boolean;
}

const settings: SettingsMenuItem[] = [
  { name: 'Profile', to: '/profile', passUserId: true, showLogin: true, showLogout: false, showOnlyAdmin: false },
  { name: 'Account', to: '/account', passUserId: true, showLogin: true, showLogout: false, showOnlyAdmin: false },
  { name: 'Login', to: '/login', passUserId: false, showLogin: false, showLogout: true, showOnlyAdmin: false },
  { name: 'Logout', to: '/logout', passUserId: false, showLogin: true, showLogout: false, showOnlyAdmin: false },
  { name: 'Apply', to: '/apply', passUserId: false, showLogin: false, showLogout: true, showOnlyAdmin: false },
];

interface GratiSettingsMenuProps {
  sx?: SxProps<Theme>;
  label?: string;
}

export default function GratiSettingsMenu(props: GratiSettingsMenuProps) {
  const sx = props.sx;
  const label = props.label;

  const { getAdminOrgs, userState } = useData();

  const { mode, setMode } = useColorScheme();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [letter, setLetter] = useState<string>('X');
  const [downloadPicUrl, setDownloadPicUrl] = useState('');

  useEffect(() => {
    if (userState.status === 'ready' && userState.userProfile) {
      if (userState.userProfile?.name?.length > 0) {
        setLetter(userState.userProfile.name.charAt(0));
      } else {
        setLetter('X');
      }
      if (userState.userProfile?.picture) {
        const profileId = userState.userProfile.itemId;
        getDownloadURL(ref(storage, `profile/${profileId}/publicFile/${userState.userProfile.picture}`))
          .then((url) => {
            setDownloadPicUrl(url);
          })
          .catch((error) => {
            console.log(error);
          });
      }
    } else {
      setLetter('X');
      setDownloadPicUrl('');
    }
  }, [userState.status, userState.userProfile]);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <Box sx={[...(Array.isArray(sx) ? sx : [sx])]}>
      <Tooltip title="Open settings">
        <IconButton onClick={handleOpenUserMenu} color="inherit" sx={{ flexDirection: 'column', px: '.5rem' }}>
          <Avatar alt={letter} src={downloadPicUrl} />
          {label && <Typography sx={{
            textAlign: "center"
          }}>{label}</Typography>}
        </IconButton>
      </Tooltip>
      <Menu
        sx={{ mt: '2.8rem' }}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
      >
        {settings.map((setting) =>
          (setting.showLogin &&
            userState.status === 'ready' &&
            (!setting.showOnlyAdmin || getAdminOrgs().length > 0)) ||
          (setting.showLogout && userState.status !== 'ready') ? (
            <MenuItem
              key={setting.name}
              component={Link}
              to={setting.passUserId ? `${setting.to}/${userState.authUser?.profileId}` : setting.to}
              onClick={handleCloseUserMenu}
            >
              <Typography sx={{
                textAlign: "center"
              }}>{setting.name}</Typography>
            </MenuItem>
          ) : null
        )}
        <Divider variant="middle" />
        <MenuItem sx={{ justifyContent: 'center' }}>
          {mode && (
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_e, newMode) => newMode && setMode(newMode)}
              aria-label="Color mode"
              size="small"
            >
              <ToggleButton value="system" aria-label="System default">
                System
              </ToggleButton>
              <ToggleButton value="dark" aria-label="Dark mode">
                Dark
              </ToggleButton>
              <ToggleButton value="light" aria-label="Light mode">
                Light
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </MenuItem>
        <Divider variant="middle" />
        <MenuItem component={Link} to="/about" onClick={handleCloseUserMenu}>
          <Typography sx={{
            textAlign: "center"
          }}>About</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
}
